import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Radio, RadioChangeEvent } from 'antd';
import { Controller, useForm, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { Modal } from '@dynamika-shared/ui/Modal/Modal';
import { Select } from '@dynamika-shared/ui/Select/Select';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { Checkbox } from '@dynamika-shared/ui/Checkbox/Checkbox';
import { NoDataSelect } from '@dynamika-shared/ui/NoDataSelect/NoDataSelect';
import {
  NotificationTypes,
  SubscriptionContent,
} from '@dynamika-shared/types/subscription';

import { useTeamMembersListSlice } from '@restore/teamMembersList/teamMembersList.slice';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';
import { TeamRoles } from 'src/constants/rolesList';
import { notificationAllowedRoles } from '../constants';
import { getAllowedSiteCodes, parseSiteCodes } from '../utils/scopeSites';

export type RecipientType = 'SYSTEM_USER' | 'EXTERNAL';

type AddEditSubscriptionModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: SubscriptionFormData) => void;
  subscription?: SubscriptionContent;
  clinicalTrialId: string;
  notificationType?: NotificationTypes;
  loading?: boolean;
  existingSubscriptions?: SubscriptionContent[];
};

const ALREADY_SUBSCRIBED_EMAIL_ERROR =
  'This email already has a subscription for this notification type';

export type SubscriptionFormData = {
  recipientType: RecipientType;
  userId: string;
  channelUserIdentifier: string;
  firstName: string;
  lastName: string;
  sites: string[];
  assignAsOwner: boolean;
};

const buildFormSchema = (
  existingEmails: Set<string>,
  existingUserIds: Set<string>
) =>
  yup.object().shape({
    recipientType: yup
      .mixed<RecipientType>()
      .oneOf(['SYSTEM_USER', 'EXTERNAL'])
      .required(),
    userId: yup
      .string()
      .defined()
      .when('recipientType', {
        is: 'SYSTEM_USER',
        then: schema =>
          schema
            .required('Please select a user')
            .test(
              'unique-user',
              'This user already has a subscription for this notification type',
              value => !value || !existingUserIds.has(value)
            ),
        otherwise: schema => schema,
      }),
    channelUserIdentifier: yup
      .string()
      .defined()
      .when('recipientType', {
        is: 'EXTERNAL',
        then: schema =>
          schema
            .required('Email address is required')
            .email('Please enter a valid email address')
            .test(
              'unique-email',
              ALREADY_SUBSCRIBED_EMAIL_ERROR,
              value => !value || !existingEmails.has(value.trim().toLowerCase())
            ),
        otherwise: schema => schema,
      }),
    firstName: yup
      .string()
      .max(50, 'First name must not exceed 50 characters')
      .default(''),
    lastName: yup
      .string()
      .max(50, 'Last name must not exceed 50 characters')
      .default(''),
    sites: yup.array().of(yup.string().defined()).defined(),
    assignAsOwner: yup.boolean().defined(),
  });

const DEFAULT_VALUES: SubscriptionFormData = {
  recipientType: 'SYSTEM_USER',
  userId: '',
  channelUserIdentifier: '',
  firstName: '',
  lastName: '',
  sites: [],
  assignAsOwner: false,
};

const isSubscriberOwner = (subscription?: SubscriptionContent): boolean =>
  Boolean(
    subscription?.userId && subscription.owners?.includes(subscription.userId)
  );

const splitFullName = (fullName: string | undefined) => {
  const trimmed = fullName?.trim() ?? '';
  if (!trimmed) {
    return { first: '', last: '' };
  }
  const lastSpace = trimmed.lastIndexOf(' ');
  if (lastSpace === -1) {
    return { first: trimmed, last: '' };
  }
  return {
    first: trimmed.slice(0, lastSpace),
    last: trimmed.slice(lastSpace + 1),
  };
};

export const AddEditSubscriptionModal: FC<AddEditSubscriptionModalProps> = ({
  open,
  onClose,
  onSubmit,
  subscription,
  clinicalTrialId,
  notificationType,
  loading = false,
  existingSubscriptions,
}) => {
  const allowedRoles = useMemo(
    () => (notificationType ? notificationAllowedRoles[notificationType] : []),
    [notificationType]
  );

  const editingSubscriptionId = subscription?.subscriptionId;

  const { existingUserIds, existingEmails } = useMemo(() => {
    const userIds = new Set<string>();
    const emails = new Set<string>();
    (existingSubscriptions ?? []).forEach(sub => {
      if (sub.subscriptionId === editingSubscriptionId) return;
      if (sub.userId) userIds.add(sub.userId);
      if (sub.channelIdentifier) {
        emails.add(sub.channelIdentifier.trim().toLowerCase());
      }
    });
    return { existingUserIds: userIds, existingEmails: emails };
  }, [existingSubscriptions, editingSubscriptionId]);

  const formSchema = useMemo(
    () => buildFormSchema(existingEmails, existingUserIds),
    [existingEmails, existingUserIds]
  );

  const [searchUserValue, setSearchUserValue] = useState('');
  const [searchSiteValue, setSearchSiteValue] = useState('');

  const sites = useClinicalTrialSlice(state => state.data?.sites ?? []);
  const [fetchTeamMembers, teamMembers, usersLoading] = useTeamMembersListSlice(
    (state, actions) => [
      actions.fetchTeamMembersListAll,
      state.allData,
      state.loading,
    ]
  );

  const allowedTeamMembers = useMemo(
    () =>
      (teamMembers ?? []).filter(
        member =>
          member.enabled && allowedRoles.includes(member.roles as TeamRoles)
      ),
    [teamMembers, allowedRoles]
  );

  const users = useMemo(() => {
    const term = searchUserValue.trim().toLowerCase();
    if (!term) return allowedTeamMembers;
    return allowedTeamMembers.filter(
      member =>
        member.fullName?.toLowerCase().includes(term) ||
        member.email?.toLowerCase().includes(term)
    );
  }, [allowedTeamMembers, searchUserValue]);

  const { formState, reset, handleSubmit, control, setValue } =
    useForm<SubscriptionFormData>({
      resolver: yupResolver<SubscriptionFormData>(formSchema),
      mode: 'onChange',
      defaultValues: DEFAULT_VALUES,
    });

  const { errors, isValid, touchedFields } = formState;
  const recipientType = useWatch({ control, name: 'recipientType' });
  const selectedUserId = useWatch({ control, name: 'userId' });
  const isSystemUser = recipientType === 'SYSTEM_USER';

  useEffect(() => {
    if (subscription) {
      const isSystemUserSub = Boolean(subscription.userId);
      const { first, last } = splitFullName(subscription.fullName);
      reset({
        recipientType: isSystemUserSub ? 'SYSTEM_USER' : 'EXTERNAL',
        userId: subscription.userId || '',
        channelUserIdentifier: subscription.channelIdentifier || '',
        firstName: isSystemUserSub ? '' : first,
        lastName: isSystemUserSub ? '' : last,
        sites: parseSiteCodes(subscription.scope),
        assignAsOwner: isSubscriberOwner(subscription),
      });
      return;
    }
    if (open) {
      reset(DEFAULT_VALUES);
      setSearchUserValue('');
      setSearchSiteValue('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription, open]);

  useEffect(() => {
    if (clinicalTrialId && open) {
      fetchTeamMembers({ clinicalTrialId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicalTrialId, open]);

  const usersOptions = useMemo(() => {
    // Users that already have a subscription for this notification type are
    // hidden from the dropdown; they reappear once their subscription is
    // deleted (existingUserIds shrinks). The currently edited subscription is
    // excluded from existingUserIds, so its user is never hidden.
    const options = users
      .filter(user => !existingUserIds.has(user.id))
      .map(user => ({
        label: user.fullName,
        value: user.id,
      }));
    const existingUserId = subscription?.userId;
    if (
      existingUserId &&
      !allowedTeamMembers.some(m => m.id === existingUserId)
    ) {
      options.unshift({
        label: subscription?.fullName ?? existingUserId,
        value: existingUserId,
      });
    }
    return options;
  }, [users, allowedTeamMembers, subscription, existingUserIds]);

  // Site users are hard-limited to the sites they belong to; the scope
  // dropdown must never offer (and a subscription must never be saved with)
  // sites outside that set. `allowedSiteCodes === null` means unrestricted
  // (non-site-user system recipients and external email recipients).
  const selectedRecipient = useMemo(
    () =>
      isSystemUser && selectedUserId
        ? (teamMembers ?? []).find(member => member.id === selectedUserId)
        : undefined,
    [isSystemUser, selectedUserId, teamMembers]
  );

  const allowedSiteCodes = useMemo(
    () => getAllowedSiteCodes(selectedRecipient),
    [selectedRecipient]
  );

  const sitesOptions = useMemo(() => {
    const term = searchSiteValue.trim().toLowerCase();
    return sites
      .filter(s => !allowedSiteCodes || (s?.code && allowedSiteCodes.has(s.code)))
      .filter(s => !term || s?.code?.toLowerCase().includes(term))
      .map(site => ({ label: site.code, value: site.code }));
  }, [sites, searchSiteValue, allowedSiteCodes]);

  // Drop any already-selected sites that fall outside the recipient's allowed
  // set — e.g. when switching the recipient to a site user, or editing a
  // subscription whose saved scope predates the recipient's current sites.
  const selectedSites = useWatch({ control, name: 'sites' });
  useEffect(() => {
    if (!allowedSiteCodes) return;
    const current = selectedSites ?? [];
    const pruned = current.filter(code => allowedSiteCodes.has(code));
    if (pruned.length !== current.length) {
      setValue('sites', pruned, { shouldValidate: true });
    }
  }, [allowedSiteCodes, selectedSites, setValue]);

  const handleUserSelect = useCallback(
    (userId: string) => {
      const user = users.find(u => u.id === userId);
      const isExistingFallback = !user && subscription?.userId === userId;
      setValue('userId', user?.id ?? (isExistingFallback ? userId : ''), {
        shouldValidate: true,
      });
      setValue(
        'channelUserIdentifier',
        user?.email ??
          (isExistingFallback ? (subscription?.channelIdentifier ?? '') : ''),
        { shouldValidate: true }
      );
    },
    [users, subscription, setValue]
  );

  const handleRecipientTypeChange = useCallback(
    (e: RadioChangeEvent) => {
      setValue('recipientType', e.target.value as RecipientType, {
        shouldValidate: true,
      });
      setValue('userId', '', { shouldValidate: true });
      setValue('channelUserIdentifier', '', { shouldValidate: true });
      setValue('firstName', '');
      setValue('lastName', '');
      setSearchUserValue('');
    },
    [setValue, setSearchUserValue]
  );

  const notFoundContent = useMemo(() => {
    if (usersLoading) {
      return null;
    }
    if (searchUserValue) {
      return (
        <NoDataSelect text={searchUserValue} maxLength={10} name='users' />
      );
    }
    // No selectable users left (none exist, or all are already subscribed).
    if (usersOptions.length === 0) {
      return <NoDataSelect maxLength={10} name='users' />;
    }
    return null;
  }, [usersLoading, searchUserValue, usersOptions.length]);

  return (
    <Modal
      width={464}
      style={{
        position: 'fixed',
        right: 24,
        top: 24,
        width: '100%',
        height: '100%',
        zIndex: 1000,
      }}
      centered
      onCancel={onClose}
      onOk={handleSubmit(onSubmit)}
      okText={subscription ? 'Save' : 'Add'}
      cancelText='Cancel'
      maskClosable={false}
      open={open}
      loading={loading}
      okProps={{ disabled: !isValid }}
      classNames={{
        body: 'fixed right-0 top-0 w-full h-full z-50 flex justify-end',
        header: 'text-left !mb-3',
        content: '!rounded-modal !p-5 !pb-0',
        footer:
          'flex justify-end gap-2 !mt-0 sticky bottom-0 bg-white !p-5 border-t border-brightGray',
      }}
      title={subscription ? 'Edit Subscription' : 'Add Subscription'}
      titleClassName='text-left font-sans text-2432 font-semibold'
      data-testid='add-edit-subscription-modal'
    >
      <div className='flex flex-col gap-3 max-h-[calc(100vh-300px)] overflow-y-auto pr-2'>
        <div className='border-b border-brightGray pb-2'>
          <div className='mb-2'>
            <label className='text-1424 font-semibold text-black'>
              Recipient type
            </label>
          </div>
          <Radio.Group
            value={recipientType}
            onChange={handleRecipientTypeChange}
            className='flex flex-col gap-2'
            data-testid='recipient-type-radio'
          >
            <Radio value='SYSTEM_USER'>Existing system user</Radio>
            <Radio value='EXTERNAL'>External email recipient</Radio>
          </Radio.Group>
        </div>

        {isSystemUser ? (
          <div className='border-b border-brightGray pb-2'>
            <Select
              showSearch
              searchValue={searchUserValue}
              onSearch={setSearchUserValue}
              placeholder='Select a User'
              filterOption={false}
              listHeight={160}
              options={usersOptions}
              value={selectedUserId || undefined}
              onChange={handleUserSelect}
              notFoundContent={notFoundContent}
              label='Select a User'
              data-testid='user-select'
              className='mb-3'
            />
            <Controller
              name='channelUserIdentifier'
              control={control}
              render={({ field }) => (
                <Input
                  label='Email'
                  {...field}
                  asText
                  data-testid='email-input'
                />
              )}
            />
          </div>
        ) : (
          <div className='border-b border-brightGray pb-3'>
            <Controller
              name='channelUserIdentifier'
              control={control}
              render={({ field }) => (
                <Input
                  label='Email'
                  placeholder='Enter email address'
                  {...field}
                  type='email'
                  autoComplete='email'
                  errors={
                    touchedFields.channelUserIdentifier
                      ? errors.channelUserIdentifier?.message
                      : undefined
                  }
                  data-testid='email-input'
                  className='mb-3'
                  required
                />
              )}
            />
            <Controller
              name='firstName'
              control={control}
              render={({ field }) => (
                <Input
                  label='First Name'
                  placeholder='Enter first name (optional)'
                  {...field}
                  errors={
                    touchedFields.firstName
                      ? errors.firstName?.message
                      : undefined
                  }
                  data-testid='firstname-input'
                  className='mb-3'
                />
              )}
            />
            <Controller
              name='lastName'
              control={control}
              render={({ field }) => (
                <Input
                  label='Last Name'
                  placeholder='Enter last name (optional)'
                  {...field}
                  errors={
                    touchedFields.lastName
                      ? errors.lastName?.message
                      : undefined
                  }
                  data-testid='lastname-input'
                />
              )}
            />
          </div>
        )}

        <div className='border-b border-brightGray pb-1'>
          <div className='mb-1'>
            <label className='text-1424 font-semibold text-black'>Scope</label>
          </div>
          <Controller
            name='sites'
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                mode='multiple'
                listHeight={160}
                options={sitesOptions}
                filterOption={false}
                searchValue={searchSiteValue}
                onSearch={setSearchSiteValue}
                onBlur={() => setSearchSiteValue('')}
                data-testid='sites-select'
                placeholder='Select sites (optional)'
              />
            )}
          />
          <p className='text-1216 text-spanishGray mt-1'>
            <span className='text-vermilion'>*</span> If no sites are selected,
            the recipient will receive notifications for all sites.
          </p>
        </div>

        {isSystemUser && (
          <div className='pb-1'>
            <Controller
              name='assignAsOwner'
              control={control}
              render={({ field: { value, onChange, ...rest } }) => (
                <Checkbox
                  {...rest}
                  checked={value}
                  onChange={e => onChange(e.target.checked)}
                  data-testid='assign-as-owner-checkbox'
                >
                  <span className='font-sans text-1424 font-medium text-black'>
                    Notify only of own actions
                  </span>
                </Checkbox>
              )}
            />
            <p className='font-sans text-1216 font-medium text-black mt-1'>
              <span className='text-vermilion'>*</span> If unchecked, all
              notifications will be sent regardless of ownership.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};
